import { Alert, Box, Skeleton } from '@mui/material';
import { TenantHierarchyDTO } from 'probonio-shared-ui/api';
import { findTenantInHierarchy, flattenTenants } from 'probonio-shared-ui/utils/tenant';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantHierarchies } from '../../module/tenant/TenantHierarchyHooks';
import TenantTreeNode from './TenantTreeNode';

interface Props {
  tenantIds: string[];
  currentTenantId: string;
  rootTenants?: TenantHierarchyDTO[];
  fetchHierarchies?: boolean;
  search?: string;
  selectedTenantId?: string;
  setSelectedTenantId?: (tenantId: string) => void;
}

function getMatchingHierarchyNodes(hierarchyNode: TenantHierarchyDTO, normalizedQuery: string): TenantHierarchyDTO[] {
  const matchingChildren = hierarchyNode.children?.flatMap(child => getMatchingHierarchyNodes(child, normalizedQuery)) ?? [];
  const matches = hierarchyNode.tenant.name.toLowerCase().includes(normalizedQuery);

  if (!matches && matchingChildren.length === 0) {
    return [];
  }
  return [{ ...hierarchyNode, children: matches ? hierarchyNode.children : matchingChildren }];
}

function getPathToNode(nodes: TenantHierarchyDTO[], targetId: string, level = 0): Record<number, string> | null {
  for (const node of nodes) {
    if (node.tenant.id === targetId) {
      return {};
    }
    if (node.children?.length) {
      const childPath = getPathToNode(node.children, targetId, level + 1);
      if (childPath !== null) {
        return { [level]: node.tenant.id, ...childPath };
      }
    }
  }
  return null;
}

function getFirstPathExpanded(nodes: TenantHierarchyDTO[], level = 0): Record<number, string> {
  const first = nodes.find(n => n.children?.length);
  if (!first) {
    return {};
  }
  return { [level]: first.tenant.id, ...getFirstPathExpanded(first.children!, level + 1) };
}

function isContainedWithin(node: TenantHierarchyDTO, nodes: TenantHierarchyDTO[]): boolean {
  return nodes.some(other => other !== node && !!findTenantInHierarchy(other, node.tenant.id));
}

function mergeExpandedByLevel(base: Record<number, string>, override: Record<number, string>): Record<number, string> {
  const result = { ...base };
  for (const [levelStr, tenantId] of Object.entries(override)) {
    const level = Number(levelStr);
    if (result[level] !== tenantId) {
      Object.keys(result).forEach(k => {
        const numKey = Number(k);
        if (numKey >= level) delete result[numKey];
      });
    }
    result[level] = tenantId;
  }
  return result;
}

function toExpandedIds(levelMap: Record<number, string>, collapsedLevel: Set<string>): Record<string, boolean> {
  return Object.fromEntries(
    Object.values(levelMap)
      .filter(id => !collapsedLevel.has(id))
      .map(id => [id, true]),
  );
}

export default function TenantTree({
  tenantIds,
  currentTenantId,
  rootTenants,
  fetchHierarchies = true,
  search = '',
  selectedTenantId: propSelectedTenantId,
  setSelectedTenantId: propSetSelectedTenantId,
}: Props) {
  const { t } = useTranslation('common');
  const [expandedLevel, setExpandedLevel] = useState<Record<number, string>>({});
  const [collapsedLevel, setCollapsedLevel] = useState<Set<string>>(new Set());
  const initializedSearchResetRef = useRef(false);
  const [internalSelectedTenantId, setInternalSelectedTenantId] = useState<string>(currentTenantId);

  const selectedTenantId = propSelectedTenantId ?? internalSelectedTenantId;
  const setSelectedTenantId = propSetSelectedTenantId ?? setInternalSelectedTenantId;
  const normalizedQuery = search.toLowerCase();
  const isSearchMode = normalizedQuery.length > 0;

  const { data, isLoading } = useTenantHierarchies(tenantIds, fetchHierarchies);
  const hierarchies = useMemo(() => {
    const roots = fetchHierarchies ? (data ?? []) : (rootTenants ?? []);
    const allNodes = new Map(roots.flatMap(flattenTenants).map(n => [n.tenant.id, n]));

    const found = tenantIds.reduce<TenantHierarchyDTO[]>((acc, id) => {
      const node = allNodes.get(id);
      if (node) acc.push(node);
      return acc;
    }, []);

    return found.filter(node => !isContainedWithin(node, found));
  }, [fetchHierarchies, data, rootTenants, tenantIds]);

  const filteredHierarchies = useMemo(
    () => (isSearchMode ? hierarchies.flatMap(h => getMatchingHierarchyNodes(h, normalizedQuery)) : hierarchies),
    [hierarchies, isSearchMode, normalizedQuery],
  );

  useEffect(() => {
    if (isLoading || isSearchMode || hierarchies.length === 0 || !selectedTenantId) {
      return;
    }
    const path = getPathToNode(hierarchies, selectedTenantId);
    setExpandedLevel(path ?? {});
    setCollapsedLevel(new Set());
  }, [isLoading, isSearchMode, hierarchies, selectedTenantId]);

  useEffect(() => {
    if (!initializedSearchResetRef.current) {
      initializedSearchResetRef.current = true;
      return;
    }
    setExpandedLevel({});
    setCollapsedLevel(new Set());
  }, [normalizedQuery]);

  const firstPathExpanded = isSearchMode ? getFirstPathExpanded(filteredHierarchies) : {};
  const levelMap = isSearchMode ? mergeExpandedByLevel(firstPathExpanded, expandedLevel) : expandedLevel;
  const expandedIds = toExpandedIds(levelMap, collapsedLevel);

  const onToggleExpand = (level: number, tenantId: string) => {
    if (expandedIds[tenantId]) {
      setCollapsedLevel(prev => new Set([...prev, tenantId]));
      setExpandedLevel(prev => {
        const next = { ...prev };
        if (next[level] === tenantId) delete next[level];
        return next;
      });
    } else {
      setCollapsedLevel(prev => {
        const next = new Set(prev);
        next.delete(tenantId);
        return next;
      });
      setExpandedLevel(prev => {
        const next = Object.fromEntries(Object.entries(prev).filter(([l]) => Number(l) < level));
        next[level] = tenantId;
        return next;
      });
    }
  };

  return isLoading ? (
    <Skeleton variant="rounded" />
  ) : (
    <Box>
      {isSearchMode && filteredHierarchies.length === 0 && <Alert severity="warning">{t('noResults')}</Alert>}
      {filteredHierarchies.map(root => (
        <TenantTreeNode
          key={root.tenant.id}
          hierarchyNode={root}
          currentTenantId={currentTenantId}
          selectedTenantId={selectedTenantId}
          expanded={expandedIds}
          onTenantSelect={setSelectedTenantId}
          onToggleExpand={onToggleExpand}
        />
      ))}
    </Box>
  );
}
